(() => {
  const SELECTORS = {
    root: '[data-partner-locator]',
    card: '[data-partner-location]',
    cardSelect: '[data-partner-location-select]',
    list: '[data-partner-location-list]',
    map: '[data-partner-location-map]',
    mapMessage: '[data-partner-map-message]',
    noResults: '[data-partner-no-results]',
    search: '[data-partner-location-search]',
    mapPanel: '[data-partner-map-panel]',
    viewButton: '[data-partner-view]'
  };

  const isReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const getCoordinates = (card) => {
    const latitude = Number.parseFloat(card.dataset.latitude);
    const longitude = Number.parseFloat(card.dataset.longitude);

    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
    if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null;

    return [latitude, longitude];
  };

  const createPopup = (card) => {
    const popup = document.createElement('div');
    popup.className = 'rymar-partner-popup';

    if (card.dataset.partner && card.dataset.partner !== card.dataset.name) {
      const partner = document.createElement('p');
      partner.className = 'rymar-partner-popup__partner';
      partner.textContent = card.dataset.partner;
      popup.appendChild(partner);
    }

    if (card.dataset.name) {
      const heading = document.createElement('h3');
      heading.textContent = card.dataset.name;
      popup.appendChild(heading);
    }

    if (card.dataset.locality) {
      const locality = document.createElement('p');
      locality.className = 'rymar-partner-popup__locality';
      locality.textContent = card.dataset.locality;
      popup.appendChild(locality);
    }

    if (card.dataset.address) {
      const address = document.createElement('p');
      address.className = 'rymar-partner-popup__address';
      address.textContent = card.dataset.address;
      popup.appendChild(address);
    }

    if (card.dataset.phone && card.dataset.phoneLink) {
      const phone = document.createElement('a');
      phone.className = 'rymar-partner-popup__phone';
      phone.href = card.dataset.phoneLink;
      phone.textContent = card.dataset.phone;
      popup.appendChild(phone);
    }

    if (card.dataset.email) {
      const email = document.createElement('a');
      email.className = 'rymar-partner-popup__email';
      email.href = `mailto:${card.dataset.email}`;
      email.textContent = card.dataset.email;
      popup.appendChild(email);
    }

    const actions = document.createElement('div');
    actions.className = 'rymar-partner-popup__actions';

    if (card.dataset.directions) {
      const directions = document.createElement('a');
      directions.className = 'rymar-partner-popup__action rymar-partner-popup__action--primary';
      directions.href = card.dataset.directions;
      directions.target = '_blank';
      directions.rel = 'noopener';
      directions.textContent = 'Directions';
      actions.appendChild(directions);
    }

    if (card.dataset.phoneLink) {
      const call = document.createElement('a');
      call.className = 'rymar-partner-popup__action';
      call.href = card.dataset.phoneLink;
      call.textContent = 'Call';
      actions.appendChild(call);
    }

    if (card.dataset.website) {
      const website = document.createElement('a');
      website.className = 'rymar-partner-popup__action';
      website.href = card.dataset.website;
      website.target = '_blank';
      website.rel = 'noopener';
      website.textContent = 'Website';
      actions.appendChild(website);
    }

    if (actions.childElementCount > 0) popup.appendChild(actions);

    return popup;
  };

  const markerIcon = (index) => window.L.divIcon({
    className: 'rymar-partner-marker-icon',
    html: `<span class="rymar-partner-marker" data-partner-marker-visual><span>${index}</span></span>`,
    iconAnchor: [19, 19],
    iconSize: [38, 38],
    popupAnchor: [0, -21]
  });

  const initializeLocator = (root) => {
    if (root.dataset.partnerLocatorReady === 'true') return;
    root.dataset.partnerLocatorReady = 'true';

    const cards = [...root.querySelectorAll(SELECTORS.card)];
    const list = root.querySelector(SELECTORS.list);
    const mapElement = root.querySelector(SELECTORS.map);
    const mapPanel = root.querySelector(SELECTORS.mapPanel);
    const mapMessage = root.querySelector(SELECTORS.mapMessage);
    const noResults = root.querySelector(SELECTORS.noResults);
    const search = root.querySelector(SELECTORS.search);
    const viewButtons = [...root.querySelectorAll(SELECTORS.viewButton)];
    const mobileLayout = window.matchMedia('(max-width: 989px)');
    const markerRecords = [];
    let map = null;
    let activeCard = null;

    const syncSelectedMapView = () => {
      if (!map) return;

      window.requestAnimationFrame(() => {
        map.invalidateSize();
        const activeRecord = markerRecords.find(({ card }) => card === activeCard && !card.hidden);

        if (activeRecord) {
          map.setView(activeRecord.coordinates, Math.max(map.getZoom(), 10), {
            animate: !isReducedMotion()
          });
          activeRecord.marker.openPopup();
        } else {
          fitVisibleMarkers();
        }
      });
    };

    const setMobileView = (view) => {
      const nextView = view === 'map' ? 'map' : 'locations';
      root.dataset.mobileView = nextView;

      viewButtons.forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.partnerView === nextView));
      });

      if (!mobileLayout.matches) {
        if (list) list.hidden = false;
        if (mapPanel) mapPanel.hidden = false;
        return;
      }

      if (list) list.hidden = nextView !== 'locations';
      if (mapPanel) mapPanel.hidden = nextView !== 'map';
      if (nextView === 'map') syncSelectedMapView();
      if (nextView === 'locations' && activeCard) {
        window.requestAnimationFrame(() => scrollCardIntoList(activeCard));
      }
    };

    const clearActive = () => {
      if (activeCard) {
        activeCard.classList.remove('is-active');
      }
      markerRecords.forEach(({ marker }) => {
        marker?.getElement()?.querySelector('[data-partner-marker-visual]')?.classList.remove('is-active');
      });
      activeCard = null;
    };

    const showMapMessage = (message) => {
      if (!mapMessage) return;
      mapMessage.textContent = message;
      mapMessage.hidden = false;
      if (mapElement) mapElement.hidden = true;
    };

    const hideMapMessage = () => {
      if (mapMessage) mapMessage.hidden = true;
      if (mapElement) mapElement.hidden = false;
    };

    const scrollCardIntoList = (card) => {
      if (!list || list.hidden) return;
      const listRect = list.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const isOutsideView = cardRect.top < listRect.top || cardRect.bottom > listRect.bottom;
      if (!isOutsideView) return;

      const targetTop = list.scrollTop + cardRect.top - listRect.top - Math.max(0, (list.clientHeight - card.offsetHeight) / 2);
      list.scrollTo({
        top: Math.max(0, targetTop),
        behavior: isReducedMotion() ? 'auto' : 'smooth'
      });
    };

    const setActive = (card, { moveMap = true, scrollCard = false } = {}) => {
      if (!card || card.hidden) return;

      clearActive();

      activeCard = card;
      activeCard.classList.add('is-active');
      const record = markerRecords.find((item) => item.card === card);

      if (record) {
        record.marker.getElement()?.querySelector('[data-partner-marker-visual]')?.classList.add('is-active');
        record.marker.openPopup();

        if (moveMap && map) {
          map.setView(record.coordinates, Math.max(map.getZoom(), 10), {
            animate: !isReducedMotion()
          });
        }
      }

      if (list && scrollCard) {
        window.requestAnimationFrame(() => scrollCardIntoList(card));
      }
    };

    const fitVisibleMarkers = () => {
      if (!map) return;

      const visibleRecords = markerRecords.filter(({ card }) => !card.hidden);
      markerRecords.forEach(({ marker, card }) => {
        if (card.hidden && map.hasLayer(marker)) map.removeLayer(marker);
        if (!card.hidden && !map.hasLayer(marker)) marker.addTo(map);
      });

      if (visibleRecords.length === 0) {
        showMapMessage('No mapped locations match your search.');
        return;
      }

      hideMapMessage();
      if (visibleRecords.length === 1) {
        map.setView(visibleRecords[0].coordinates, 9, { animate: false });
      } else {
        map.fitBounds(window.L.latLngBounds(visibleRecords.map(({ coordinates }) => coordinates)), {
          animate: false,
          maxZoom: 10,
          padding: [42, 42]
        });
      }
    };

    if (window.L && mapElement) {
      cards.forEach((card) => {
        const coordinates = getCoordinates(card);
        if (!coordinates) return;

        markerRecords.push({ card, coordinates, marker: null });
      });

      if (markerRecords.length > 0) {
        hideMapMessage();
        map = window.L.map(mapElement, { scrollWheelZoom: false });
        window.L.tileLayer(root.dataset.tileUrl, {
          attribution: root.dataset.tileAttribution,
          maxZoom: 19
        }).addTo(map);

        markerRecords.forEach((record) => {
          record.marker = window.L.marker(record.coordinates, {
            icon: markerIcon(record.card.dataset.locationIndex),
            keyboard: true,
            title: record.card.dataset.name || 'Partner location'
          }).addTo(map);
          record.marker.bindPopup(createPopup(record.card));
          record.marker.on('click', () => setActive(record.card, { moveMap: false, scrollCard: true }));
        });

        fitVisibleMarkers();
        window.setTimeout(() => map.invalidateSize(), 0);
      } else {
        showMapMessage('Map locations will appear when coordinates are available.');
      }
    } else {
      showMapMessage('The interactive map is temporarily unavailable. Location details remain available in the list.');
    }

    cards.forEach((card) => {
      const select = card.querySelector(SELECTORS.cardSelect);
      if (!select) return;
      select.addEventListener('click', () => setActive(card));
      select.addEventListener('focus', () => setActive(card));
    });

    viewButtons.forEach((button) => {
      button.addEventListener('click', () => setMobileView(button.dataset.partnerView));
    });

    const handleLayoutChange = () => {
      setMobileView(root.dataset.mobileView);
      if (!mobileLayout.matches && map) window.requestAnimationFrame(() => map.invalidateSize());
    };

    if (mobileLayout.addEventListener) {
      mobileLayout.addEventListener('change', handleLayoutChange);
    } else {
      mobileLayout.addListener(handleLayoutChange);
    }
    setMobileView('locations');

    search?.addEventListener('input', () => {
      const query = search.value.trim().toLocaleLowerCase();
      let visibleCount = 0;

      cards.forEach((card) => {
        const matches = !query || card.dataset.search.includes(query);
        card.hidden = !matches;
        if (matches) visibleCount += 1;
      });

      if (noResults) noResults.hidden = visibleCount !== 0;
      if (activeCard?.hidden) {
        clearActive();
      }
      fitVisibleMarkers();
    });
  };

  const initializeAll = () => {
    document.querySelectorAll(SELECTORS.root).forEach((root) => initializeLocator(root));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll(SELECTORS.root).forEach((root) => initializeLocator(root));
  });
})();
